import { Injectable, inject } from '@angular/core';
import { Borders, Workbook, Worksheet } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { BPTable, CCEDewPointReports, CCEReports, DPTable, SummaryOfBPTable, SummaryOfCCEReports } from '../../models/lab-test';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFCceRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    private commonHeadStylesWithoutBorder = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonBodyStylesWithoutBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonStylesOthers = {
        fontSize: 10,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    private commonHeadStylesOthers = {
        fontSize: 12,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 2 },
        lineWidth: 0
    };

    private commonBodyStylesOthers = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    CCE(doc: jsPDF, marginSize: number, CCEExp: CCEReports, wellName: string, sampleName: string): void {
        if (CCEExp !== undefined && CCEExp !== null) {
            marginSize += 15;
            const val = CCEExp;
            let isLastRowDrawn = false;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(wellName + ' ' + sampleName, marginSize, currentY);
            currentY += 5;

            let headerValues = [];
            headerValues = reportConstants.headerValuesCCEPDF;
            autoTable(doc, {
                head: headerValues,
                body: val.bpTable.map((val: BPTable) => [
                    val.pressure ?? '',
                    val.pointName ?? '',
                    val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '',
                    val.lVMassDens!=null?val.lVMassDens.toFixed(4) ?? '' : '',
                    val.compressibility!=null?val.compressibility.toFixed(2) ?? '' : '',
                    val.liquidVolume != null ? val.liquidVolume.toFixed(2) ?? '' : ''
                ]),
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize, top: 40 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didDrawPage: function (data) {
                    let pageY = 30;
                    doc.setFontSize(12);
                    doc.setFont(reportConstants.eMprintCondensedBold);
                    doc.text(reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp, marginSize, pageY);
                    pageY += 5;
                    doc.setFont(reportConstants.eMprintCondensedRegular);
                    doc.text(wellName + ' ' + sampleName, marginSize, pageY);

                    if (isLastRowDrawn) {
                        const boxX = marginSize;
                        const boxY = (data.cursor && typeof data.cursor.y === reportConstants.number) ? data.cursor.y : 0;
                        const boxWidth = doc.internal.pageSize.getWidth() - marginSize * 2;
                        const lineHeight = 5;
                        const boxHeight = lineHeight * 5 + 6;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(boxX, boxY, boxWidth, boxHeight);
                        doc.setFontSize(10);
                        doc.setFont(reportConstants.eMprintCondensedRegular);
                        const textY = boxY + lineHeight;
                        doc.text(reportConstants.comments, boxX + 2, textY);

                        doc.text(reportConstants.CCEComments1, boxX + 2, textY + lineHeight);
                        doc.text(reportConstants.CCEComments2, boxX + 2, textY + lineHeight * 2);
                        doc.text(reportConstants.CCEComments3, boxX + 2, textY + lineHeight * 3);
                        doc.text(reportConstants.CCEComments4, boxX + 2, textY + lineHeight * 4);
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 2) {
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    const rowCount = val.bpTable.length;
                    if (data.row.index === rowCount - 1 && data.section === reportConstants.body) {
                        isLastRowDrawn = true;
                    }
                }
            });
            if (val.graphData.length > 0) {
                doc.addPage();

                doc.setFont(reportConstants.eMprintCondensedBold);
                let currentY = 30;
                doc.text(reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp, marginSize, currentY);
                currentY += 5;
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(wellName + ' ' + sampleName, marginSize, currentY);
                currentY += 5;

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;
                const imageHeight = imageWidth * 0.8;

                const xPos = (pageWidth - imageWidth) / 2;
                let yPos = currentY;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    val.graphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

                yPos += imageHeight - 20;

                doc.addImage(
                    val.graphData[1],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );
            }
        }
    }

    CCEDewPoint(doc: jsPDF, marginSize: number, CCEDewPointExp: CCEDewPointReports, wellName: string, sampleName: string): void {
        if (CCEDewPointExp !== undefined && CCEDewPointExp !== null) {
            marginSize += 5;
            const val = CCEDewPointExp;
            let isLastRowDrawn = false;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(wellName + ' ' + sampleName, marginSize, currentY);
            currentY += 5;

            let headerValues = [];
            headerValues = reportConstants.headerValuesCCEDewPDF;

            autoTable(doc, {
                head: headerValues,
                body: val.dpTable.map((val: DPTable) => 
                    [
                        val.pressure, 
                        val.pointName, 
                        val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '', 
                        val.lVMassDens != null ? val.lVMassDens.toFixed(4) ?? '' : '', 
                        val.zFactor != null ? val.zFactor.toFixed(2) ?? '' : '', 
                        val.liquidVolumeStg != null ? val.liquidVolumeStg.toFixed(2) ?? '' : '', 
                        val.liquidVolumeSat != null ? val.liquidVolumeSat.toFixed(2) ?? '' : ''
                    ]),
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize, top: 40 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didDrawPage: function (data) {
                    let pageY = 30;
                    doc.setFontSize(12);
                    doc.setFont(reportConstants.eMprintCondensedBold);
                    doc.text(reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp, marginSize, pageY);
                    pageY += 5;
                    doc.setFont(reportConstants.eMprintCondensedRegular);
                    doc.text(wellName + ' ' + sampleName, marginSize, pageY);

                    if (isLastRowDrawn) {
                        const boxX = marginSize;
                        const boxY = (data.cursor && typeof data.cursor.y === reportConstants.number) ? data.cursor.y : 0;
                        const boxWidth = doc.internal.pageSize.getWidth() - marginSize * 2;
                        const lineHeight = 5;
                        const boxHeight = lineHeight * 5 + 6;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(boxX, boxY, boxWidth, boxHeight);
                        doc.setFontSize(10);
                        doc.setFont(reportConstants.eMprintCondensedRegular);
                        const textY = boxY + lineHeight;
                        doc.text(reportConstants.comments, boxX + 2, textY);

                        doc.text(reportConstants.CCEComments1, boxX + 2, textY + lineHeight);
                        doc.text(reportConstants.cceDewSwellComments2, boxX + 2, textY + lineHeight * 2);
                        doc.text(reportConstants.cceDewComments3, boxX + 2, textY + lineHeight * 3);
                        doc.text(reportConstants.cceDewComments4, boxX + 2, textY + lineHeight * 4);
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 2) {
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);
                    }
                    const rowCount = val.dpTable.length;
                    if (data.row.index === rowCount - 1 && data.section === reportConstants.body) {
                        isLastRowDrawn = true;
                    }
                }
            });
            if (val.graphData.length > 0) {
                doc.addPage();

                doc.setFont(reportConstants.eMprintCondensedBold);
                let currentY = 30;
                doc.text(reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp, marginSize, currentY);
                currentY += 5;
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(wellName + ' ' + sampleName, marginSize, currentY);
                currentY += 5;

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;
                const imageHeight = imageWidth * 0.8;

                const xPos = (pageWidth - imageWidth) / 2;
                let yPos = currentY;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    val.graphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

                yPos += imageHeight - 20;

                doc.addImage(
                    val.graphData[1],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );
            }
        }
    }

    CCESepGas(doc: jsPDF, marginSize: number, CCEExp: CCEReports, wellName: string, molPercentage = 0): void {
        if (CCEExp !== undefined && CCEExp !== null) {
            marginSize += 15;
            const val = CCEExp;
            let isLastRowDrawn = false;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.swellingTest + ': ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas, marginSize, currentY);
            currentY += 5;

            let headerValues = [];
            headerValues = reportConstants.headerValuesCCEPDF;
            autoTable(doc, {
                head: headerValues,
                body: val.bpTable.map((val: BPTable) => [
                    val.pressure ?? '',
                    val.pointName ?? '',
                    val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '',
                    val.lVMassDens!=null?val.lVMassDens.toFixed(4) ?? '' : '',
                    val.compressibility!=null?val.compressibility.toFixed(2) ?? '' : '',
                    val.liquidVolume != null ? val.liquidVolume.toFixed(2) ?? '' : ''
                ]),
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize, top: 40 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didDrawPage: function (data) {
                    let pageY = 30;
                    doc.setFontSize(12);
                    doc.setFont(reportConstants.eMprintCondensedBold);
                    doc.text(wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp, marginSize, pageY);
                    pageY += 5;
                    doc.setFont(reportConstants.eMprintCondensedRegular);
                    doc.text(reportConstants.swellingTest + ': ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas, marginSize, pageY);
                    if (isLastRowDrawn) {
                        const boxX = marginSize;
                        const boxY = (data.cursor && typeof data.cursor.y === reportConstants.number) ? data.cursor.y : 0;
                        const boxWidth = doc.internal.pageSize.getWidth() - marginSize * 2;
                        const lineHeight = 5;
                        const boxHeight = lineHeight * 5 + 6;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(boxX, boxY, boxWidth, boxHeight);
                        doc.setFontSize(10);
                        doc.setFont(reportConstants.eMprintCondensedRegular);
                        const textY = boxY + lineHeight;
                        doc.text(reportConstants.comments, boxX + 2, textY);

                        doc.text(reportConstants.CCEComments1, boxX + 2, textY + lineHeight);
                        doc.text(reportConstants.CCEComments2, boxX + 2, textY + lineHeight * 2);
                        doc.text(reportConstants.CCEComments3, boxX + 2, textY + lineHeight * 3);
                        doc.text(reportConstants.CCEComments4, boxX + 2, textY + lineHeight * 4);
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 2) {
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    const rowCount = val.bpTable.length;
                    if (data.row.index === rowCount - 1 && data.section === reportConstants.body) {
                        isLastRowDrawn = true;
                    }
                }
            });
            if (val.graphData.length > 0) {
                doc.addPage();

                doc.setFont(reportConstants.eMprintCondensedBold);
                let currentY = 30;
                doc.text(wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp, marginSize, currentY);
                currentY += 5;
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.swellingTest + ': ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas, marginSize, currentY);
                currentY += 5;

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;
                const imageHeight = imageWidth * 0.8;

                const xPos = (pageWidth - imageWidth) / 2;
                let yPos = currentY;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    val.graphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

                yPos += imageHeight - 20;

                doc.addImage(
                    val.graphData[1],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );
            }
        }
    }

    CCEDewPointSepGas(doc: jsPDF, marginSize: number, CCEExp: CCEDewPointReports, wellName: string, molPercentage = 0): void {
        if (CCEExp !== undefined && CCEExp !== null) {
            marginSize += 15;
            const val = CCEExp;
            let isLastRowDrawn = false;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            doc.text(wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp, marginSize, currentY);
            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.swellingTest + ': ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas, marginSize, currentY);
            currentY += 5;

            let headerValues = [];
            headerValues = reportConstants.headerValuesCCEDewPDF;

            autoTable(doc, {
                head: headerValues,
                body: val.dpTable.map((val: DPTable) => [
                        val.pressure, 
                        val.pointName, 
                        val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '', 
                        val.lVMassDens != null ? val.lVMassDens.toFixed(4) ?? '' : '', 
                        val.zFactor != null ? val.zFactor.toFixed(2) ?? '' : '', 
                        val.liquidVolumeStg != null ? val.liquidVolumeStg.toFixed(2) ?? '' : '', 
                        val.liquidVolumeSat != null ? val.liquidVolumeSat.toFixed(2) ?? '' : ''
                ]),
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize, top: 40 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didDrawPage: function (data) {
                    let pageY = 30;
                    doc.setFontSize(12);
                    doc.setFont(reportConstants.eMprintCondensedBold);
                    doc.text(wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp, marginSize, pageY);
                    pageY += 5;
                    doc.setFont(reportConstants.eMprintCondensedRegular);
                    doc.text(reportConstants.swellingTest + ': ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas, marginSize, pageY);
                    if (isLastRowDrawn) {
                        const boxX = marginSize;
                        const boxY = (data.cursor && typeof data.cursor.y === reportConstants.number) ? data.cursor.y : 0;
                        const boxWidth = doc.internal.pageSize.getWidth() - marginSize * 2;
                        const lineHeight = 5;
                        const boxHeight = lineHeight * 5 + 6;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(boxX, boxY, boxWidth, boxHeight);
                        doc.setFontSize(10);
                        doc.setFont(reportConstants.eMprintCondensedRegular);
                        const textY = boxY + lineHeight;
                        doc.text(reportConstants.comments, boxX + 2, textY);

                        doc.text(reportConstants.CCEComments1, boxX + 2, textY + lineHeight);
                        doc.text(reportConstants.cceDewSwellComments2, boxX + 2, textY + lineHeight * 2);
                        doc.text(reportConstants.cceDewComments3, boxX + 2, textY + lineHeight * 3);
                        doc.text(reportConstants.cceDewComments4, boxX + 2, textY + lineHeight * 4);
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 2) {
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    const rowCount = val.dpTable.length;
                    if (data.row.index === rowCount - 1 && data.section === reportConstants.body) {
                        isLastRowDrawn = true;
                    }
                }
            });
            if (val.graphData.length > 0) {
                doc.addPage();

                doc.setFont(reportConstants.eMprintCondensedBold);
                let currentY = 30;
                doc.text(wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp, marginSize, currentY);
                currentY += 5;
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.swellingTest + ': ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas, marginSize, currentY);
                currentY += 5;

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;
                const imageHeight = imageWidth * 0.8;

                const xPos = (pageWidth - imageWidth) / 2;
                let yPos = currentY;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    val.graphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

                yPos += imageHeight - 20;

                doc.addImage(
                    val.graphData[1],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );
            }
        }
    }

    SummaryofCCE(doc: jsPDF, marginSize: number, CCEExp: SummaryOfCCEReports, wellName: string, sampleName: string, appendixText = ''): void {
        if (CCEExp !== undefined && CCEExp !== null) {
            marginSize += 10;
            const val = CCEExp;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            if (appendixText === '') {
                doc.text(reportConstants.summaryOfCCEExperiments, marginSize, currentY);
            }
            else {
                doc.text(appendixText + reportConstants.CCEData, marginSize, currentY);
            }
            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(wellName + ' ' + sampleName, marginSize, currentY);
            currentY += 5;

            let headerValues = [];
            headerValues = reportConstants.summaryOfCCEheaderValues;
            autoTable(doc, {
                head: headerValues,
                body: val.bpTable.map((val: SummaryOfBPTable) => [
                    val.temp ?? '',
                    val.psat ?? '',
                    val.systemType ?? '',
                    val.relativeVolume,
                    val.densityAtPsat != null ? val.densityAtPsat.toFixed(4) ?? '' : ''
                ]),
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize + 10, right: marginSize + 10, top: 40 },
                styles: this.commonStylesOthers as Partial<Styles>,
                headStyles: this.commonHeadStylesOthers as Partial<Styles>,
                bodyStyles: this.commonBodyStylesOthers as Partial<Styles>,
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 2) {
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                }
            });
            if (val.graphData.length > 0) {

                currentY += 25;

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;

                const xPos = (pageWidth - imageWidth) / 2;
                const yPos = currentY;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    val.graphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    yPos,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

                if (doc.lastAutoTable) {
                    currentY = doc.lastAutoTable.finalY + currentY + 70;
                }

                doc.setFontSize(9);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(reportConstants.tableHeaderNote, marginSize - 5, currentY);
            }
        }
    }

    async GenerateCCE(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, sampleName: string, subHeader: string) {
        try {
            const cceWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cceWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cceWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 0 automatic
                fitToHeight: 1,// 0 automatic
                scale: 45,
                printTitlesRow: '9:9',
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.10,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            await DisplayLogoAndWellName(this, cceWorksheet);

            if (CCEExp !== undefined && CCEExp !== null) {
                const val = CCEExp;

                DisplaySheetTitle(cceWorksheet, val);

                let rowNo = 6;
                const tableHeader = reportConstants.tableHeaderCCE;
                const tableBody = val.bpTable.map((val: BPTable) => [
                    val.pressure?.toString() ?? '',
                    val.pointName?.toString() ?? '',
                    val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '',
                    val.lVMassDens != null ? val.lVMassDens.toFixed(4) ?? '' : '',
                    val.compressibility != null ? val.compressibility.toFixed(2) ?? '' : '',
                    val.liquidVolume != null ? val.liquidVolume.toFixed(2) ?? '' : ''
                ]);

                const tableStartRow = rowNo++;
                this.GenerateTableWithBorders(cceWorksheet, tableHeader, tableBody, tableStartRow, 2, borderStyle);
                for (let headerRowIndex = 0; headerRowIndex < tableHeader.length; headerRowIndex++) {
                    cceWorksheet.getRow(tableStartRow + headerRowIndex).height = 45;
                }
                rowNo = tableStartRow + tableHeader.length + tableBody.length;
                rowNo += 2;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.comments);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments1);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments2);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments3);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments4);

                rowNo += 1;
                this.AddOuterBorder(cceWorksheet, 4, rowNo, 1, 8);
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitle(worksheet: Worksheet, val: CCEReports) {
            worksheet.mergeCells(reportConstants.b4, reportConstants.h4);
            const textExperimentNameCell = worksheet.getCell(reportConstants.b4);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textExperimentNameCell.value = reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

            worksheet.mergeCells(reportConstants.b5, reportConstants.h5);
            const textWellName = worksheet.getCell(reportConstants.b5);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = wellName + ' ' + sampleName;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.c;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.o : reportConstants.h;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + reportConstants.two);
            }
        }
    }

    async GenerateCCEPlots(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, sampleName: string, subHeader: string) {
        try {
            const val = CCEExp;
            if (val.graphData.length > 0) {
                const ccePlotsWorksheet = workbook.addWorksheet(worksheetName);
                ccePlotsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
                this._reportGeneralUtilityService.ApplyFooterAndPageSetup(ccePlotsWorksheet, undefined, {
                    paperSize: undefined, // letter
                    orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                    fitToPage: true,
                    fitToWidth: 1,
                    fitToHeight: 1,
                    scale: 60,
                    margins: {
                        left: 0.25,
                        right: 0.25,
                        top: 0.25,
                        bottom: 0.25,
                        header: 0,
                        footer: 0
                    }
                });
                await DisplayLogoAndWellName(this, ccePlotsWorksheet);
                DisplaySheetTitle(ccePlotsWorksheet, val);


                const vVsatId = workbook.addImage({
                    base64: val.graphData[0],
                    extension: reportConstants.png,
                });

                ccePlotsWorksheet.mergeCells(reportConstants.a10, reportConstants.k34);
                ccePlotsWorksheet.addImage(vVsatId, {
                    tl: { col: 1, row: 10 },
                    ext: { width: 600, height: 400 }
                });


                const liquidVolId = workbook.addImage({
                    base64: val.graphData[1],
                    extension: reportConstants.png,
                });

                ccePlotsWorksheet.mergeCells(reportConstants.a36, reportConstants.k51);
                ccePlotsWorksheet.addImage(liquidVolId, {
                    tl: { col: 1, row: 36 },
                    ext: { width: 600, height: 400 }
                });

                this.AddOuterBorder(ccePlotsWorksheet, 5, 60, 1, 11);
            }

        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitle(worksheet: Worksheet, val: CCEReports) {
            worksheet.mergeCells(reportConstants.b5, reportConstants.j6);
            const textExperimentNameCell = worksheet.getCell(reportConstants.b5);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textExperimentNameCell.value = reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

            worksheet.mergeCells(reportConstants.b7, reportConstants.h8);
            const textWellName = worksheet.getCell(reportConstants.b7);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = wellName + ' ' + sampleName;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.j;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.k : reportConstants.m;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + reportConstants.two);
            }
        }
    }

    async GenerateCCEDewPoint(workbook: Workbook, worksheetName: string, CCEDewPointExp: CCEDewPointReports, wellName: string, sampleName: string, subHeader: string) {
        try {
            const cceDewPointWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cceDewPointWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cceDewPointWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,//page 1
                fitToHeight: 1, // 1 page , 0 automatic,
                scale: 50,
                printTitlesRow: '9:9',
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.10,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            await DisplayLogoAndWellName(this, cceDewPointWorksheet);

            if (CCEDewPointExp !== undefined && CCEDewPointExp !== null) {
                const val = CCEDewPointExp;
                DisplaySheetTitle(cceDewPointWorksheet, val);

                let rowNo = 6;
                const tableHeader = reportConstants.tableHeaderCCEDEW;

                const tableBody = val.dpTable.map((val: DPTable) => [
                    val.pressure?.toString() ?? '',
                    val.pointName?.toString() ?? '',
                    val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '', 
                    val.lVMassDens != null ? val.lVMassDens.toFixed(4) ?? '' : '', 
                    val.zFactor != null ? val.zFactor.toFixed(2) ?? '' : '', 
                    val.liquidVolumeStg != null ? val.liquidVolumeStg.toFixed(2) ?? '' : '', 
                    val.liquidVolumeSat != null ? val.liquidVolumeSat.toFixed(2) ?? '' : ''
                ]);

                const tableStartRow = rowNo++;
                this.GenerateTableWithBorders(cceDewPointWorksheet, tableHeader, tableBody, tableStartRow, 2, borderStyle);
                for (let headerRowIndex = 0; headerRowIndex < tableHeader.length; headerRowIndex++) {
                    cceDewPointWorksheet.getRow(tableStartRow + headerRowIndex).height = 45;
                }
                rowNo = tableStartRow + tableHeader.length + tableBody.length;
                rowNo += 2;

                this.AddComment(cceDewPointWorksheet, reportConstants.b, rowNo, reportConstants.comments);
                rowNo++;
                this.AddComment(cceDewPointWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments1);
                rowNo++;
                this.AddComment(cceDewPointWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments2);
                rowNo++;
                this.AddComment(cceDewPointWorksheet, reportConstants.b, rowNo, reportConstants.cceDewComments3);
                rowNo++;
                this.AddComment(cceDewPointWorksheet, reportConstants.b, rowNo, reportConstants.cceDewComments4);

                rowNo += 2;
                this.AddOuterBorder(cceDewPointWorksheet, 4, rowNo, 1, 9);
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitle(worksheet: Worksheet, val: CCEDewPointReports) {
            worksheet.mergeCells(reportConstants.b4, reportConstants.h4);
            const textExperimentNameCell = worksheet.getCell(reportConstants.b4);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textExperimentNameCell.value = reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

            worksheet.mergeCells(reportConstants.b5, reportConstants.h5);
            const textWellName = worksheet.getCell(reportConstants.b5);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = wellName + ' ' + sampleName;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.c;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.o : reportConstants.i;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + reportConstants.two);
            }
        }
    }

    async GenerateCCEDewPlots(workbook: Workbook, worksheetName: string, CCEDewPointExp: CCEDewPointReports, wellName: string, sampleName: string, subHeader: string) {
        try {
            const ccePlotsWorksheet = workbook.addWorksheet(worksheetName);
            if (CCEDewPointExp !== undefined && CCEDewPointExp !== null) {
                const val = CCEDewPointExp;
                DisplaySheetTitle(ccePlotsWorksheet, val);
                if (val.graphData.length > 0) {
                    ccePlotsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
                    this._reportGeneralUtilityService.ApplyFooterAndPageSetup(ccePlotsWorksheet, undefined, {
                        paperSize: undefined, // A4
                        orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                        fitToPage: true,
                        fitToWidth: 1,//page 1
                        fitToHeight: 1, // 1 page , 0 automatic 
                        scale: 60,
                        margins: {
                            left: 0.25,
                            right: 0.25,
                            top: 0.25,
                            bottom: 0.25,
                            header: 0,
                            footer: 0
                        }
                    });
                    await DisplayLogoAndWellName(this, ccePlotsWorksheet);

                    const vVsatId = workbook.addImage({
                        base64: val.graphData[0],
                        extension: reportConstants.png,
                    });

                    ccePlotsWorksheet.mergeCells(reportConstants.a10, reportConstants.k34);
                    ccePlotsWorksheet.addImage(vVsatId, {
                        tl: { col: 1, row: 10 },
                        ext: { width: 600, height: 400 }
                    });

                    const liquidVolId = workbook.addImage({
                        base64: val.graphData[1],
                        extension: reportConstants.png,
                    });

                    ccePlotsWorksheet.mergeCells(reportConstants.a36, reportConstants.k51);
                    ccePlotsWorksheet.addImage(liquidVolId, {
                        tl: { col: 1, row: 36 },
                        ext: { width: 600, height: 400 }
                    });

                    this.AddOuterBorder(ccePlotsWorksheet, 5, 60, 1, 11);
                }
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitle(worksheet: Worksheet, val: CCEDewPointReports) {
            worksheet.mergeCells(reportConstants.b5, reportConstants.j6);
            const textExperimentNameCell = worksheet.getCell(reportConstants.b5);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textExperimentNameCell.value = reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

            worksheet.mergeCells(reportConstants.b7, reportConstants.h8);
            const textWellName = worksheet.getCell(reportConstants.b7);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = wellName + ' ' + sampleName;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {

            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.j;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.k : reportConstants.m;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + reportConstants.two);
            }
        }
    }

    async GenerateCCESepGas(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, molPercentage = 0,subHeader: string) {
        try {
            const cceWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cceWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cceWorksheet, undefined, {
                paperSize: undefined, // Legal
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 0 automatic
                fitToHeight: 1,// 0 automatic
                scale: 44,
                printTitlesRow: '1:8',
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.10,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            await DisplayLogoAndWellName(this, cceWorksheet);

            if (CCEExp !== undefined && CCEExp !== null) {
                const val = CCEExp;

                DisplaySheetTitle(cceWorksheet, val);

                let rowNo = 6;
                const tableHeader = reportConstants.tableHeaderSwellingCCE;
                const tableBody = val.bpTable.map((val: BPTable) => [
                    val.pressure?.toString() ?? '',
                    val.pointName?.toString() ?? '',
                    val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '',
                    val.lVMassDens != null ? val.lVMassDens.toFixed(4) ?? '' : '',
                    val.compressibility != null ? val.compressibility.toFixed(2) ?? '' : '',
                    val.liquidVolume != null ? val.liquidVolume.toFixed(2) ?? '' : ''
                ]);


                const tableStartRow = rowNo++;
                this.GenerateTableWithBorders(cceWorksheet, tableHeader, tableBody, tableStartRow, 2, borderStyle);
                for (let headerRowIndex = 0; headerRowIndex < tableHeader.length; headerRowIndex++) {
                    cceWorksheet.getRow(tableStartRow + headerRowIndex).height = 45;
                }
                rowNo = tableStartRow + tableHeader.length + tableBody.length;
                rowNo += 1;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.comments);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments1);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments2);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments3);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments4);

                rowNo += 1;
                this.AddOuterBorder(cceWorksheet, 3, rowNo, 1, 9);


            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitle(worksheet: Worksheet, val: CCEReports) {
            worksheet.mergeCells(reportConstants.b3, reportConstants.i4);
            const textExperimentNameCell = worksheet.getCell(reportConstants.b3);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textExperimentNameCell.value = wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };

            worksheet.mergeCells(reportConstants.b5, reportConstants.h5);
            const textWellName = worksheet.getCell(reportConstants.b5);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = reportConstants.swellingTest.replace('-', ':') + ' ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.c;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.j : reportConstants.i;
                worksheet.mergeCells(columnStart + reportConstants.one, columnEnd + '2');
                const textWellName = worksheet.getCell(columnStart + reportConstants.one);
                textWellName.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
                textWellName.value = subHeader;
                textWellName.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
            }
        }
    }

    async GenerateCCEGasPlots(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, molPercentage = 0,subHeader: string) {

        const val = CCEExp;
        if (val.graphData.length > 0) {
            const ccePlotsWorksheet = workbook.addWorksheet(worksheetName);

            ccePlotsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(ccePlotsWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 0 automatic
                fitToHeight: 1,// 0 automatic
                scale: 70,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            await DisplayLogoAndWellName(this, ccePlotsWorksheet);
            DisplaySheetTitle(ccePlotsWorksheet, val);

           


            const vVsatId = workbook.addImage({
                base64: val.graphData[0],
                extension: reportConstants.png,
            });
            ccePlotsWorksheet.mergeCells(reportConstants.a10, reportConstants.k34);
            ccePlotsWorksheet.addImage(vVsatId, {
                tl: { col: 1, row: 10 },
                ext: { width: 600, height: 400 }
            });


            const liquidVolId = workbook.addImage({
                base64: val.graphData[1],
                extension: reportConstants.png,
            });
            ccePlotsWorksheet.mergeCells(reportConstants.a36, reportConstants.k51);
            ccePlotsWorksheet.addImage(liquidVolId, {
                tl: { col: 1, row: 36 },
                ext: { width: 600, height: 400 }
            });

            this.AddOuterBorder(ccePlotsWorksheet, 5, 60, 1, 11);
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.c;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.k : reportConstants.i;
                worksheet.mergeCells(columnStart + reportConstants.one, columnEnd + '2');
                const textWellName = worksheet.getCell(columnStart + reportConstants.one);
                textWellName.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
                textWellName.value = subHeader;
                textWellName.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
            }
        }
        function DisplaySheetTitle(worksheet: Worksheet, val: CCEReports) {
            worksheet.mergeCells(reportConstants.a5, reportConstants.k6);
            const textExperimentNameCell = worksheet.getCell(reportConstants.a5);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle,wrapText: true };
            textExperimentNameCell.value = wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };

            worksheet.mergeCells(reportConstants.a7, reportConstants.h8);
            const textWellName = worksheet.getCell(reportConstants.b7);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = reportConstants.swellingTest.replace('-', ':') + ' ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }
    }

    async GenerateCCEDewPointSepGas(workbook: Workbook, worksheetName: string, CCEExp: CCEDewPointReports, wellName: string, molPercentage = 0,subHeader: string) {
        try {
            const cceWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cceWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cceWorksheet, undefined, {
                paperSize: undefined, // Legal
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 0 automatic
                fitToHeight: 1,// 0 automatic
                scale: 50,
                printTitlesRow: '9:9',
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.10,
                    bottom: 0.10,
                    header: 0,
                    footer: 0
                }
            });

            await DisplayLogoAndWellName(this, cceWorksheet);

            if (CCEExp !== undefined && CCEExp !== null) {
                const val = CCEExp;

                DisplaySheetTitle(cceWorksheet, val);

                let rowNo = 5;
                const tableHeader = reportConstants.tableHeaderSwellingDew;
                const tableBody = val.dpTable.map((val: DPTable) => [
                    val.pressure?.toString() ?? '',
                    val.pointName?.toString() ?? '',
                    val.vVSat != null ? val.vVSat.toFixed(4) ?? '' : '', 
                    val.lVMassDens != null ? val.lVMassDens.toFixed(4) ?? '' : '', 
                    val.zFactor != null ? val.zFactor.toFixed(2) ?? '' : '', 
                    val.liquidVolumeStg != null ? val.liquidVolumeStg.toFixed(2) ?? '' : '', 
                    val.liquidVolumeSat != null ? val.liquidVolumeSat.toFixed(2) ?? '' : ''
                ]);

                const tableStartRow = rowNo++;
                this.GenerateTableWithBorders(cceWorksheet, tableHeader, tableBody, tableStartRow, 2, borderStyle, 11);
                for (let headerRowIndex = 0; headerRowIndex < tableHeader.length; headerRowIndex++) {
                    cceWorksheet.getRow(tableStartRow + headerRowIndex).height = 35;
                }
                rowNo = tableStartRow + tableHeader.length + tableBody.length;
                rowNo += 1;

                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.comments);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.CCEComments1);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.cceDewSwellComments2);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.cceDewComments3);
                rowNo++;
                this.AddComment(cceWorksheet, reportConstants.b, rowNo, reportConstants.cceDewComments4);
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitle(worksheet: Worksheet, val: CCEDewPointReports) {
            worksheet.mergeCells(reportConstants.b3, reportConstants.i3);
            const textExperimentNameCell = worksheet.getCell(reportConstants.b3);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };
            textExperimentNameCell.value = wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + '' + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };

            worksheet.mergeCells(reportConstants.b4, reportConstants.h4);
            const textWellName = worksheet.getCell(reportConstants.b4);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = reportConstants.swellingTest.replace('-', ':') + ' ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.c;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.o : reportConstants.i;
                worksheet.mergeCells(columnStart + reportConstants.one, columnEnd + '2');
                const textWellName = worksheet.getCell(columnStart + reportConstants.one);
                textWellName.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
                textWellName.value = subHeader;
                textWellName.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
            }
        }
    }

    async GenerateCCEDewPointGasPlots(workbook: Workbook, worksheetName: string, CCEExp: CCEDewPointReports, wellName: string, sampleName: string, molPercentage = 0, subHeader: string) {
        const val = CCEExp;
        if (val.graphData.length > 0) {
            const ccePlotsWorksheet = workbook.addWorksheet(worksheetName);;

            ccePlotsWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(ccePlotsWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 0 automatic
                fitToHeight: 1,// 0 automatic
                scale: 70,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            await DisplayLogoAndWellName(this, ccePlotsWorksheet);
            DisplaySheetTitle(ccePlotsWorksheet, val);

            const vVsatId = workbook.addImage({
                base64: val.graphData[0],
                extension: reportConstants.png,
            });

            ccePlotsWorksheet.mergeCells(reportConstants.a10, reportConstants.k34);
            ccePlotsWorksheet.addImage(vVsatId, {
                tl: { col: 1, row: 10 },
                ext: { width: 600, height: 400 }
            });


            const liquidVolId = workbook.addImage({
                base64: val.graphData[1],
                extension: reportConstants.png,
            });

            ccePlotsWorksheet.mergeCells(reportConstants.a36, reportConstants.k51);
            ccePlotsWorksheet.addImage(liquidVolId, {
                tl: { col: 1, row: 36 },
                ext: { width: 600, height: 400 }
            });

            this.AddOuterBorder(ccePlotsWorksheet, 5, 60, 1, 11);
        }
        function DisplaySheetTitle(worksheet: Worksheet, val: CCEDewPointReports) {
            worksheet.mergeCells(reportConstants.a5, reportConstants.k6);
            const textExperimentNameCell = worksheet.getCell(reportConstants.a5);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };
            textExperimentNameCell.value = wellName + ' ' + molPercentage + ' ' + reportConstants.molPercentSepGas + '' + ' ' + reportConstants.constantCompositionExpansionAt + ' ' + val.cceDewPointProperties.studyTemperature + reportConstants.fTemp;
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };

            worksheet.mergeCells(reportConstants.a7, reportConstants.g8);
            const textWellName = worksheet.getCell(reportConstants.a7);
            textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            textWellName.value = reportConstants.swellingTest.replace('-', ':') + ' ' + molPercentage + reportConstants.percentage + ' ' + reportConstants.experimentNameSeparatorGas;
            textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = worksheet.name.includes(reportConstants.plot) ? reportConstants.c : reportConstants.j;
                const columnEnd = worksheet.name.includes(reportConstants.plot) ? reportConstants.k : reportConstants.m;
                worksheet.mergeCells(columnStart + reportConstants.one, columnEnd + '2');
                const textWellName = worksheet.getCell(columnStart + reportConstants.one);
                textWellName.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };
                textWellName.value = subHeader;
                textWellName.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
            }
        }
    }

    async GenerateSummaryOfCCE(workbook: Workbook, worksheetName: string, CCEExp: SummaryOfCCEReports, wellName: string, sampleName: string, fromCall = '', letter = '', reportType: string, subHeader: string) {
        try {

            const cceWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cceWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cceWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 60,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            await DisplayLogoAndWellName(this, cceWorksheet);

            if (CCEExp !== undefined && CCEExp !== null) {
                const val = CCEExp;
                if (reportType === reportConstants.reportTypeSeparatorTest) {
                    DisplaySheetTitleSummary(cceWorksheet, fromCall, false, letter, reportType);
                } else {
                    DisplaySheetTitleSummary(cceWorksheet, fromCall, true, letter, reportType);
                }

                let rowNo = 9;
                const tableHeader = reportConstants.tableHeader;
                const tableBody = val.bpTable.map((val: SummaryOfBPTable) => [
                    val.temp?.toString() ?? '',
                    val.psat?.toString() ?? '',
                    val.systemType?.toString() ?? '',
                    val.relativeVolume?.toString() ?? '',
                    val.densityAtPsat != null ? val.densityAtPsat.toFixed(4) ?? '' : ''
                ]);
                const tableStartRow = rowNo++;
                this.GenerateTableWithBorders(cceWorksheet, tableHeader, tableBody, tableStartRow, 3, borderStyle);
                for (let headerRowIndex = 0; headerRowIndex < tableHeader.length; headerRowIndex++) {
                    cceWorksheet.getRow(tableStartRow + headerRowIndex).height = 45;
                }
                rowNo = tableStartRow + tableHeader.length + tableBody.length;
                rowNo += 2;

                if (val.graphData.length > 0) {

                    const vVsatId = workbook.addImage({
                        base64: val.graphData[0],
                        extension: reportConstants.png,
                    });


                    cceWorksheet.addImage(vVsatId, {
                        tl: { col: 2, row: rowNo },
                        ext: { width: 600, height: 400 }
                    });

                    // Add outer border from C5 to N63
                    rowNo = 40;
                    this.AddComment(cceWorksheet, reportConstants.a, rowNo, reportConstants.tableHeaderNote, 10);
                    this.AddOuterBorder(cceWorksheet, 5, 46, 1, 10);
                }
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function DisplaySheetTitleSummary(worksheet: Worksheet, from = '', subText = true, letter = '', report = '') {
            if (from === reportConstants.appendix) {
                worksheet.mergeCells(reportConstants.b5, reportConstants.i6);
            }
            else {
                worksheet.mergeCells(reportConstants.b5, reportConstants.h6);
            }
            const textExperimentNameCell = worksheet.getCell(reportConstants.b5);
            textExperimentNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            if (from === reportConstants.appendix) {
                if (report === reportConstants.reportTypeSeparatorTest) {
                    textExperimentNameCell.value = reportConstants.appendix + ' ' + letter + ': ' + wellName + ' ' + reportConstants.summaryOfCCEExperiments;
                }
                else {
                    textExperimentNameCell.value = reportConstants.appendix + ' ' + letter + ': ' + reportConstants.CCEData;
                }
            } else {
                textExperimentNameCell.value = reportConstants.summaryOfCCEExperiments;
            }
            textExperimentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };

            if (subText) {
                worksheet.mergeCells(reportConstants.b7, reportConstants.h8);
                const textWellName = worksheet.getCell(reportConstants.b7);
                textWellName.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                textWellName.value = wellName + ' ' + sampleName;
                textWellName.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            }
        }

        async function DisplayLogoAndWellName(report: ReportPDFCceRenderService, worksheet: Worksheet) {
            worksheet.getColumn(reportConstants.b).width = 5;
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            worksheet.mergeCells(reportConstants.a1, reportConstants.b2);
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, reportConstants.c1, reportConstants.j2);
        }
    }

    public AddOuterBorder(
        worksheet: Worksheet,
        startRowNo: number,
        endRowNo: number,
        startColumnNo: number,
        endColumnNo: number
    ) {
        for (let r = startRowNo; r <= endRowNo; r++) {
            for (let c = startColumnNo; c <= endColumnNo; c++) {
                const cell = worksheet.getCell(r, c);
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                cell.border = {
                    top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }

    GenerateTableWithBorders(
        worksheet: Worksheet,
        tableHeader: string[][],
        tableBody: (string | number)[][],
        tableStartRow: number,
        tableStartColumn: number,
        borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } },
        bodyFontSize = 11
    ) {
        tableHeader.forEach((headerRow, idx) => {
            headerRow.forEach((header, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                cell.value = header;
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                cell.border = {
                    top: idx === 0 ? { style: reportConstants.thin } : borderStyle.top,
                    bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.thin } : borderStyle.left,
                    right: colIdx === headerRow.length - 1 ? { style: reportConstants.thin } : borderStyle.right,
                } as Partial<Borders>;

                const headerValue = header.split('\n');
                if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                    worksheet.getColumn(tableStartColumn + colIdx).width = headerValue[0].length + 2;
                } else {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 8;
                }
                worksheet.getRow(tableStartRow + idx).height = 35; // set the row height for header rows
            });
        });

        tableBody.forEach((row, rowIdx) => {
            row.forEach((cellValue, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                setNumericOrTextCellValue(cell, cellValue);
                cell.font = { name: reportConstants.eMprintCondensed, size: bodyFontSize, bold: false };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                cell.border = {
                    bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.thin } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.thin } : borderStyle.left,
                    right: colIdx === row.length - 1 ? { style: reportConstants.thin } : borderStyle.right,
                } as Partial<Borders>;
            });
        });
    }

    AddComment(worksheet: Worksheet, column: string, rowNo: number, value: string, fontSize = 11) {
        const textCommentsCell = worksheet.getCell(column + rowNo);
        textCommentsCell.value = value;
        textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: fontSize, bold: false };
        textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        worksheet.getRow(rowNo).height = 15;
    }
}